/** Native print-to-PDF: vector, selectable text, no PDF dependency and no
 *  worker round-trip — the same mechanism the SOP Compliance page uses. The
 *  rendered report already carries the tenant's OWN department name (the
 *  sandbox copy rebrands draft_json), so what a prospect saves says their
 *  department on it, not ours. document.title seeds the saved filename.
 *
 *  Shared rather than local to one view: the report screen offers this from two
 *  places (the report header, and the draft editor's action row next to Save
 *  draft / Finalize) and they must produce the identical document. Anything the
 *  PDF should not contain — the type tabs, the edit form — carries `print:hidden`
 *  at its own site, so what prints is the report itself.
 */
import { formatDateTime } from "@/lib/format";

/** Owned here, not at the call sites. Both the printed letterhead and the
 *  saved filename derive from it, so a caller that substituted its own
 *  fallback would produce one document naming two different departments. */
export const DEFAULT_DEPT_NAME = "Fire Department";

/** Fire-service convention: a 24-hour clock in the department's own zone
 *  (RES-164, which the server renderer follows). `toLocaleString()` here would
 *  put "8/10/2026, 5:54:45 PM" in the page footer of a document whose every
 *  other stamp reads "2026-05-18 07:35 NDT". */
function generatedStamp(tz?: string): string {
  return formatDateTime(new Date().toISOString(), tz);
}

let restoreTitle: string | null = null;

/** Quote a value as a CSS string literal for a custom property.
 *
 *  A department name is tenant-supplied text and lands inside `content: "..."`.
 *  An unescaped double quote would close the string and truncate the rest of
 *  the declaration, silently blanking the running header. Mirrors the
 *  `css_string` Jinja filter on the WeasyPrint side.
 */
function cssString(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/[\r\n]+/g, " ")}"`;
}

/** The page furniture — running header and footer — is drawn by `@page` margin
 *  boxes (globals.css), which is the one mechanism Chromium and WeasyPrint
 *  both support. Margin boxes cannot read the DOM, and `string()`/`string-set`
 *  is WeasyPrint-only, so the text reaches them as custom properties on the
 *  root element. Set for the duration of the print, cleared after: they exist
 *  for the printed copy, not for the app.
 */
const PRINT_VARS = ["--print-dept", "--print-title", "--print-provenance"];

export function printReport(
  deptName: string | null | undefined,
  reportTitle: string,
  /** Department timezone, when the caller has it. Without one the stamp falls
   *  back to the reader's own zone rather than silently claiming a zone. */
  tz?: string,
) {
  // Only the FIRST call captures the title. A second call before `afterprint`
  // would otherwise capture the already-rewritten title as the thing to
  // restore, stranding the tab named after a report.
  if (restoreTitle === null) restoreTitle = document.title;
  const dept = deptName || DEFAULT_DEPT_NAME;
  document.title = `${dept} — ${reportTitle}`;

  const root = document.documentElement;
  root.style.setProperty("--print-dept", cssString(dept));
  root.style.setProperty("--print-title", cssString(reportTitle));
  root.style.setProperty(
    "--print-provenance",
    cssString(`Generated ${generatedStamp(tz)} · RespAI`),
  );

  const restore = () => {
    if (restoreTitle !== null) document.title = restoreTitle;
    restoreTitle = null;
    for (const name of PRINT_VARS) root.style.removeProperty(name);
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  window.print();
}
